import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Users, Building2, FolderKanban, Sparkles, Clock } from "lucide-react";
import { ActivityLogsList } from "./ActivityLogsList";

const OverviewTabContent = ({ departmentCount }: { departmentCount: number }) => {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Departments
            </CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{departmentCount}</div>
            <p className="text-xs text-muted-foreground">
              Active departments in your organization
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Projects
            </CardTitle>
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12</div>
            <p className="text-xs text-muted-foreground">
              Active projects across departments
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Documents
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">43</div>
            <p className="text-xs text-muted-foreground">
              Shared documents and resources
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              AI Insights
            </CardTitle>
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">7</div>
            <p className="text-xs text-muted-foreground">
              Generated insights and recommendations
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest 3 activities across your organization
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ActivityLogsList />
          </CardContent>
        </Card>
        <UpcomingDeadlines />
      </div>
    </div>
  );
};

const ActivityFeed = () => (
  <Card className="col-span-1">
    <CardHeader>
      <CardTitle>Recent Activity</CardTitle>
      <CardDescription>
        Your team's activity in the past 24 hours
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <ActivityItem 
          icon={Users} 
          title="New team member added" 
          description="Sarah Johnson joined Engineering" 
          time="2h ago" 
        />
        <ActivityItem 
          icon={FileText} 
          title="Document updated" 
          description="Q4 Marketing Strategy updated" 
          time="5h ago" 
        />
        <ActivityItem 
          icon={FolderKanban} 
          title="New project created" 
          description="Website Redesign project added" 
          time="8h ago" 
        />
      </div>
    </CardContent>
  </Card>
);

const ActivityItem = ({ 
  icon: Icon, 
  title, 
  description, 
  time 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  time: string;
}) => (
  <div className="flex items-center gap-4 rounded-lg border p-3">
    <div className="rounded-full bg-primary/10 p-2">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <div className="ml-auto text-xs text-muted-foreground">
      {time}
    </div>
  </div>
);

const UpcomingDeadlines = () => (
  <Card className="col-span-1">
    <CardHeader>
      <CardTitle>Upcoming Deadlines</CardTitle>
      <CardDescription>
        Project milestones due in the next 7 days
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-4">
        <DeadlineItem 
          title="Content Approval" 
          department="Marketing Department" 
          dueIn="Tomorrow" 
          priority="high" 
        />
        <DeadlineItem 
          title="API Integration" 
          department="Engineering Department" 
          dueIn="3 days" 
          priority="medium" 
        />
        <DeadlineItem 
          title="Budget Review" 
          department="Finance Department" 
          dueIn="6 days" 
          priority="low" 
        />
      </div>
    </CardContent>
  </Card>
);

const DeadlineItem = ({ 
  title, 
  department, 
  dueIn, 
  priority 
}: { 
  title: string; 
  department: string; 
  dueIn: string; 
  priority: "high" | "medium" | "low";
}) => {
  const priorityStyles = {
    high: "bg-red-100 text-red-700",
    medium: "bg-orange-100 text-orange-700",
    low: "bg-yellow-100 text-yellow-700"
  };

  const textStyles = {
    high: "text-red-600",
    medium: "text-orange-600",
    low: "text-yellow-600"
  };

  return (
    <div className="flex items-center gap-4 rounded-lg border p-3">
      <div className={`rounded-full ${priorityStyles[priority]} p-2`}>
        <Clock className="h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{department}</p>
      </div>
      <div className={`ml-auto text-xs font-medium ${textStyles[priority]}`}>
        {dueIn}
      </div>
    </div>
  );
};

export default OverviewTabContent;
